/**
 * Klientský WS subscription manager. Komponenty (page, TopBar, karty) deklarativně
 * říkají, co potřebují přes `useWsSubscription(spec)`. Modul agreguje všechny aktivní
 * fragmenty do jedné efektivní subscription a debouncedně posílá `socket.emit("subscribe", spec)`.
 *
 * Pattern (hybrid):
 *  - page komponenta zavolá `useWsSubscription({ mqttTopics: [...] })` při mount,
 *    fragment se odregistruje při unmount
 *  - sdílené komponenty (TopBar, AverageValue v topbaru) přidávají vlastní fragmenty
 *  - efektivní subscription = union všech aktivních fragmentů
 *
 * Server udržuje per-socket subscription state (`src/server/services/wsSubscriptions.ts`)
 * a filtruje WS emity. Viz docs/plans/WS-TRAFFIC-REDUCTION.md (Fáze 3).
 */

import { useEffect, useId } from "react";
import { socket } from "@client/ws-client";

export type WsSubscriptionFragment = {
  mqttTopics?: string[];
  /** BMS slugy pro full telemetrii. */
  bmsFull?: string[];
  /** BMS overview (subset polí) pro VŠECHNY slugy. */
  bmsOverview?: boolean;
  pipFull?: string[];
  pipOverview?: boolean;
  tracerFull?: string[];
  tracerOverview?: boolean;
  /** Systémový monitor: hostitelé, jejichž souhrn sledujeme (jen ADMIN). */
  sysmonHosts?: string[];
  /** Dotaz na tabulku procesů; null = proc box mimo obrazovku. */
  sysmonProc?: WsSysmonProcQuery | null;
};

const fragments = new Map<string, WsSubscriptionFragment>();
let lastSentSerialized: string | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const buildEffectiveSpec = (): WsSubscription => {
  const mqttTopics = new Set<string>();
  const bmsFull = new Set<string>();
  const pipFull = new Set<string>();
  const tracerFull = new Set<string>();
  let bmsOverview = false;
  let pipOverview = false;
  let tracerOverview = false;

  const sysmonHosts = new Set<string>();
  // Sysmon má jediného producenta (SystemPage) — union dotazu na procesy nedává smysl,
  // proto se bere první nalezený.
  let sysmonProc: WsSysmonProcQuery | null = null;

  for (const f of fragments.values()) {
    f.mqttTopics?.forEach((t) => mqttTopics.add(t));
    f.bmsFull?.forEach((s) => bmsFull.add(s));
    f.pipFull?.forEach((s) => pipFull.add(s));
    f.tracerFull?.forEach((s) => tracerFull.add(s));
    if (f.bmsOverview) bmsOverview = true;
    if (f.pipOverview) pipOverview = true;
    if (f.tracerOverview) tracerOverview = true;
    f.sysmonHosts?.forEach((h) => sysmonHosts.add(h));
    if (!sysmonProc && f.sysmonProc) sysmonProc = f.sysmonProc;
  }

  const spec: WsSubscription = {
    mqttTopics: [...mqttTopics].sort(),
    bms: { full: [...bmsFull].sort(), overview: bmsOverview },
    pip: { full: [...pipFull].sort(), overview: pipOverview },
    tracer: { full: [...tracerFull].sort(), overview: tracerOverview },
  };

  // Klíč se přidává jen když je co posílat — jinak by `JSON.stringify` identita
  // (a s ní deduplikace emitů) reagovala na prázdný objekt.
  if (sysmonHosts.size > 0 || sysmonProc) {
    spec.sysmon = { hosts: [...sysmonHosts].sort(), proc: sysmonProc };
  }

  return spec;
};

const sendIfChanged = (force: boolean = false): void => {
  if (!socket.connected) return;
  const spec = buildEffectiveSpec();
  const serialized = JSON.stringify(spec);
  if (!force && serialized === lastSentSerialized) return;
  lastSentSerialized = serialized;
  socket.emit("subscribe", spec);
};

const scheduleFlush = (): void => {
  if (debounceTimer) clearTimeout(debounceTimer);
  // 50 ms debounce — rychlé navigace mezi page (mount/unmount více komponent) sloučí
  // do jednoho emitu, aby server nedostával několik subscribe za sebou.
  debounceTimer = setTimeout(() => sendIfChanged(false), 50);
};

/**
 * Po reconnectu pošle aktuální subscription (server po `connect` všechno zapomněl,
 * pokud nešlo o connection state recovery). Volá ws-client v `socket.on("connect")`.
 */
export const flushSubscription = (): void => {
  lastSentSerialized = null;
  sendIfChanged(true);
};

/**
 * Deklarativní subscription fragment vázaný na životnost komponenty. Při změně `spec`
 * (referenční) se fragment přepíše a manager pošle updatovanou efektivní subscription.
 *
 * UPOZORNĚNÍ: předávej buď stabilní reference nebo memoizuj v `useMemo`. Jinak hook
 * při každém renderu přeregistruje fragment a posílá zbytečné subscribe eventy.
 */
export const useWsSubscription = (spec: WsSubscriptionFragment): void => {
  const id = useId();
  useEffect(() => {
    fragments.set(id, spec);
    scheduleFlush();
    return () => {
      fragments.delete(id);
      scheduleFlush();
    };
  }, [id, spec]);
};
